import { Component, Inject, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import Swal from "sweetalert2";

import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { NgxIntlTelInputModule } from "ngx-intl-tel-input";
import {
  SearchCountryField,
  CountryISO,
  PhoneNumberFormat,
} from "ngx-intl-tel-input";

import { GloPhonenumberInputComponent } from "../../../shared/components/glo-phonenumber-input/glo-phonenumber-input.component";
import { GloDatePickerComponent } from "../../../shared/components/glo-date-picker/glo-date-picker/glo-date-picker.component";
import { GoogleAddressComponent } from "../../../shared/google-address/google-address.component";

import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { ApiService } from "../../../shared/services/api/api.service";
import { Add_Salon } from "../../../shared/models/salon.model";
import { CountryService } from "../../../shared/services/country.service";
import { catchError, of } from "rxjs";

type SalonTab = "overview" | "location" | "contact" | "media";

@Component({
  selector: "app-add-salon-form",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    GloDatePickerComponent,
    GloPhonenumberInputComponent,
    NgxIntlTelInputModule,
    LoaderComponent,
    GoogleAddressComponent,
  ],
  templateUrl: "./add-salon.component.html",
  styleUrls: ["./add-salon.component.scss"],
})
export class AddSalonFormComponent implements OnInit {
  salonForm: FormGroup;

  loading = false;
  isEditMode = false;
  salonId: string | null = null;

  images: File[] = [];
  previewUrls: string[] = [];

  countries: { code: string; name: string }[] = [];

  activeTab: SalonTab = "overview";

  tabs: { key: SalonTab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "location", label: "Location" },
    { key: "contact", label: "Contact" },
    { key: "media", label: "Media" },
  ];

  separateDialCode = true;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;

  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.UnitedKingdom,
  ];

  customCssClass = "form-control rounded-[8px] w-full block";

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private countryService: CountryService,
    private dialogRef: MatDialogRef<AddSalonFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.salonId = data?.salonId ? String(data.salonId) : null;
    this.isEditMode = !!this.salonId;

    this.salonForm = this.fb.group({
      name: ["", Validators.required],
      ownerFirstName: ["", Validators.required],
      ownerLastName: ["", Validators.required],
      phone: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      address: ["", [Validators.required, Validators.minLength(5)]],
      city: ["", [Validators.required, Validators.minLength(2)]],
      state: ["", Validators.required],
      postalCode: ["", Validators.required],
      country: ["", Validators.required],
      latitude: [null],
      longitude: [null],
      businessType: ["", Validators.required],
      yearsInBusiness: ["", Validators.required],
      numberOfStaff: ["", Validators.required],
      description: [""],
      images: [[]],
    });

    if (this.isEditMode && data?.salonData) {
      this.populateForm(data.salonData);
    }
  }

  ngOnInit(): void {
    this.countries = this.countryService.getCountries();

    if (this.isEditMode && this.salonId && !this.data?.salonData) {
      this.loadSalonDetail(this.salonId);
    }
  }

  loadSalonDetail(id: string): void {
    this.loading = true;

    this.apiService
      .request<Add_Salon>(API_ENDPOINTS.salons.detail(id), "GET")
      .pipe(
        catchError(() => {
          this.loading = false;
          return of({} as Add_Salon);
        }),
      )
      .subscribe({
        next: (response: Add_Salon) => {
          this.loading = false;
          this.populateForm(response);
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  populateForm(data: any): void {
    if (!data) return;

    this.salonForm.patchValue({
      name: data.name || "",
      ownerFirstName: data.ownerFirstName || "",
      ownerLastName: data.ownerLastName || "",
      phone: data.phone || "",
      email: data.email || "",
      address: data.address || "",
      city: data.city || "",
      state: data.state || "",
      postalCode: data.postalCode || "",
      country: data.country || "",
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      businessType: data.businessType || "",
      yearsInBusiness: data.yearsInBusiness || "",
      numberOfStaff: data.numberOfStaff || "",
      description: data.description || "",
      images: data.images || [],
    });

    if (Array.isArray(data.images)) {
      this.previewUrls = data.images.filter(
        (image: any) => typeof image === "string",
      );
    }
  }

  get addressControl(): FormControl {
    return this.fetchInputField("address");
  }

  get addressInvalid(): boolean {
    const ctrl = this.addressControl;
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  onAddressSelected(place: google.maps.places.PlaceResult): void {
    const parts = this.parsePlace(place);

    this.salonForm.patchValue(
      {
        address: parts.street || parts.formattedAddress,
        city: parts.city,
        state: parts.state,
        country: parts.country,
        postalCode: parts.postalCode,
        latitude: parts.lat,
        longitude: parts.lng,
      },
      { emitEvent: true },
    );

    ["address", "city", "state", "country", "postalCode"].forEach((key) => {
      this.salonForm.get(key)?.markAsTouched();
      this.salonForm.get(key)?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private parsePlace(place: google.maps.places.PlaceResult) {
    const comps = place.address_components ?? [];

    const pick = (
      type: string,
      which: "long_name" | "short_name" = "long_name",
    ) => comps.find((c) => (c.types || []).includes(type))?.[which] ?? "";

    const lat = place.geometry?.location?.lat?.();
    const lng = place.geometry?.location?.lng?.();

    const city =
      pick("locality") ||
      pick("postal_town") ||
      pick("sublocality") ||
      pick("sublocality_level_1");

    const state = pick("administrative_area_level_1", "short_name");
    const country = pick("country", "long_name");
    const postalCode = pick("postal_code", "long_name");

    const streetNumber = pick("street_number", "long_name");
    const route = pick("route", "long_name");
    const street = [streetNumber, route].filter(Boolean).join(" ").trim();

    const formattedAddress =
      place.formatted_address ||
      (street ? `${street}${city ? ", " + city : ""}` : "") ||
      "";

    return {
      formattedAddress,
      street,
      city,
      state,
      country,
      postalCode,
      lat: typeof lat === "number" ? lat : null,
      lng: typeof lng === "number" ? lng : null,
    };
  }

  onSubmit(): void {
    if (this.salonForm.invalid) {
      this.salonForm.markAllAsTouched();
      this.goToFirstInvalidTab();
      return;
    }

    const salonData = this.salonForm.value;
    salonData.phone = salonData.phone?.e164Number || salonData.phone || "";

    this.loading = true;

    if (this.isEditMode) {
      this.updateSalon(salonData);
    } else {
      this.createSalon(salonData);
    }
  }

  private goToFirstInvalidTab(): void {
    const overviewFields = [
      "name",
      "ownerFirstName",
      "ownerLastName",
      "businessType",
      "yearsInBusiness",
      "numberOfStaff",
    ];

    const locationFields = [
      "address",
      "city",
      "state",
      "postalCode",
      "country",
    ];

    const contactFields = ["phone", "email"];

    if (overviewFields.some((field) => this.salonForm.get(field)?.invalid)) {
      this.activeTab = "overview";
      return;
    }

    if (locationFields.some((field) => this.salonForm.get(field)?.invalid)) {
      this.activeTab = "location";
      return;
    }

    if (contactFields.some((field) => this.salonForm.get(field)?.invalid)) {
      this.activeTab = "contact";
    }
  }

  private createSalon(salonData: any): void {
    this.apiService
      .request(
        API_ENDPOINTS.salons.create,
        "POST",
        salonData,
        "Salon Created Successfully!",
      )
      .subscribe({
        next: () => {
          this.loading = false;
          this.dialogRef.close(true);

          Swal.fire({
            title: "Salon Created Successfully!",
            icon: "success",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
        error: () => {
          this.loading = false;

          Swal.fire({
            title: "Failed to Create Salon",
            text: "Error while creating salon. Please try again.",
            icon: "error",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
      });
  }

  private updateSalon(salonData: any): void {
    if (!this.salonId) return;

    this.apiService
      .request(
        API_ENDPOINTS.salons.update(String(this.salonId)),
        "PATCH",
        salonData,
        "Salon Updated Successfully!",
      )
      .subscribe({
        next: () => {
          this.loading = false;
          this.dialogRef.close(true);

          Swal.fire({
            title: "Salon Updated Successfully!",
            icon: "success",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
        error: () => {
          this.loading = false;

          Swal.fire({
            title: "Failed to Update Salon",
            text: "Error while updating salon. Please try again.",
            icon: "error",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
      });
  }

  checkValidField(fieldName: string): boolean {
    const field = this.salonForm.get(fieldName);

    if (!field) return true;

    return !(field.invalid && field.touched);
  }

  validationChecker(): boolean {
    return !!(
      this.salonForm.get("phone")?.touched &&
      this.salonForm.get("phone")?.invalid
    );
  }

  fetchInputField(controlName: string): FormControl {
    return this.salonForm.get(controlName) as FormControl;
  }

  handleFileSelect(event: any): void {
    const files = Array.from(event.target.files) as File[];
    if (!files.length) return;
    this.addFiles(files);
  }

  handleDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  handleDrop(event: DragEvent): void {
    event.preventDefault();

    const files = Array.from(event.dataTransfer?.files || []);
    this.addFiles(files);
  }

  addFiles(files: File[]): void {
    const imagesLength = this.images.length;
    const allowedLength = 5 - imagesLength;

    if (imagesLength >= 5) {
      this.showUploadWarning();
      return;
    }

    if (files.length > allowedLength) {
      files = files.slice(0, allowedLength);
      this.showUploadWarning();
    }

    for (const file of files) {
      if (file.size < 5 * 1024 * 1024) {
        this.images.push(file);

        const reader = new FileReader();
        reader.onload = (event: any) =>
          this.previewUrls.push(event.target.result);
        reader.readAsDataURL(file);
      } else {
        Swal.fire({
          title: "Error while uploading file",
          text: "File size should be less than 5 MB.",
          icon: "error",
          timer: 5000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      }
    }

    this.salonForm.get("images")?.setValue(this.images);
    this.salonForm.get("images")?.updateValueAndValidity();
  }

  removeImage(index: number): void {
    this.images.splice(index, 1);
    this.previewUrls.splice(index, 1);
    this.salonForm.get("images")?.setValue(this.images);
  }

  private showUploadWarning(): void {
    Swal.fire({
      title: "Error while uploading files",
      text: "You can upload maximum 5 files",
      icon: "warning",
      timer: 5000,
      showConfirmButton: false,
      toast: true,
      position: "top-end",
    });
  }

  get modalTitle(): string {
    return this.isEditMode ? "Edit Salon" : "Add Salon";
  }

  get readableBusinessType(): string {
    const value = this.salonForm.get("businessType")?.value;

    if (!value) return "Not set";

    return String(value)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  onClose(): void {
    this.dialogRef.close(false);
  }
}